import React, { useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import usePortal from 'react-useportal';
import Cloaser from '../Cloaser';

import { InvitePopup, LeavePopup, MobileRequiredPopup, SettingsPopup } from '../../../Popup';

import { showPopupWindow } from '../../../../redux/actions/Popups';
import { userLogout } from '../../../../redux/actions/User';

import { dataForSidebar } from '../../../../redux/selector/Sidebar';
import { getUser, isAuth } from '../../../../redux/selector/User';
import { getPopupByName } from '../../../../redux/selector/Popups';
import { getSeasonNumber } from '../../../../redux/selector/Team';

import { cashCompact } from '../../../../service/functions';
import useOutsideClick from '../../../../service/hooks';

import { enableTourReplay, resetAllTours } from '../../../../helpers/tour/locationToStorage';
import { allowTour } from '../../../../redux/actions/TourControl';

import {
  StyledHeader,
  StyledDesktopBar,
  StyledMobileBar,
  StyledBrand,
  StyledHeaderLeft,
  StyledHeaderRight,
  StyledCrestLink,
  StyledTeamBlock,
  StyledTeamName,
  StyledMetaRow,
  StyledMetaLink,
  StyledMetaIcon,
  StyledAvatar,
  StyledDropdownTrigger,
  StyledDiscordTrigger,
  StyledDropdownWrapper,
  StyledDropdownContainer,
  DiscordLogoIcon,
} from './styled';
import UserDropdown from '../../../UserDropdown';
import ClubSwitcher from '../../../ClubSwitcher';
import NotificationBell from '../../../NotificationBell';
import { POPUPS } from '../../../../service/constants';
import useClubSwitchOptions from '../../../../hooks/useClubSwitchOptions';
import { ReactComponent as DollarIcon } from '../../../../assets/img/header/dollar-sign.svg';
import { ReactComponent as GlobeIcon } from '../../../../assets/img/header/globe.svg';
import tolLogo from '../../../../assets/img/tol-logo-horizontal.png';

export default React.memo(function Header({ onClick, isSideOpen = false }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const data = useSelector(dataForSidebar);
  const isAuthenticated = useSelector(isAuth);
  const user = useSelector(getUser);
  const seasonNumber = useSelector(getSeasonNumber);
  const invitePopupData = useSelector(getPopupByName(POPUPS.INVITE_POPUP));
  const settingsPopupData = useSelector(getPopupByName(POPUPS.SETTINGS_POPUP));
  const leavePopupData = useSelector(getPopupByName(POPUPS.LEAVE_POPUP));
  const mobileRequiredData = useSelector(
    getPopupByName(POPUPS.MOBILE_REQUIRED_POPUP)
  );

  const avatarUrl = `https://cdn.discordapp.com/avatars/${user.discord}/${user.avatarID}.jpg`;
  const [isDropdownVisible, showDropdown] = useState(false);
  const ref = useRef();
  useOutsideClick(ref, () => {
    if (isDropdownVisible) showDropdown(false);
  });

  React.useEffect(() => {
    if (user.leaveTeam) {
      localStorage.removeItem('isFromRegister');
      history.push('/');
    }
  }, [user.leaveTeam, history]);
  const toggleDropdown = () => {
    showDropdown(!isDropdownVisible);
  };

  const openInvitePopup = () => {
    showDropdown(false);
    dispatch(showPopupWindow(POPUPS.INVITE_POPUP, POPUPS.INVITE_POPUP));
  };

  const openSettingsPopup = () => {
    showDropdown(false);
    dispatch(showPopupWindow(POPUPS.SETTINGS_POPUP, POPUPS.SETTINGS_POPUP));
  };

  const showTutorial = () => {
    enableTourReplay();
    resetAllTours();
    showDropdown(false);
    dispatch(allowTour(true));
  };

  const { currentTeam, currentServer } = useClubSwitchOptions();

  const logout = () => {
    dispatch(userLogout());
    localStorage.removeItem('isFromRegister');
  };

  const leaveGame = () => {
    dispatch(showPopupWindow(POPUPS.LEAVE_POPUP, POPUPS.LEAVE_POPUP));
  };

  const { Portal } = usePortal({
    bindTo: document && document.querySelector('#modal'),
  });

  const crest = currentTeam?.symbol || user.team_info?.symbol;
  const clubName = currentTeam?.name || user.team_info?.name || 'Club';
  const seasonValue = Number(seasonNumber);
  const seasonLabel =
    seasonNumber != null &&
    seasonNumber !== '' &&
    Number.isFinite(seasonValue)
      ? `S${seasonValue}`
      : null;
  const serverMeta = [currentServer, seasonLabel].filter(Boolean).join(' | ');

  const renderMetaRow = () => (
    <StyledMetaRow>
      <StyledMetaLink
        to="/finance"
        data-negative={data.cash < 0 ? 'true' : 'false'}
      >
        <StyledMetaIcon>
          <DollarIcon />
        </StyledMetaIcon>
        {cashCompact(data.cash)}
      </StyledMetaLink>
      {serverMeta ? (
        <StyledMetaLink to="/league">
          <StyledMetaIcon>
            <GlobeIcon />
          </StyledMetaIcon>
          {serverMeta}
        </StyledMetaLink>
      ) : null}
    </StyledMetaRow>
  );

  return (
    <StyledHeader>
      {!isEmpty(invitePopupData) && (
        <Portal>
          <InvitePopup popupName={POPUPS.INVITE_POPUP} />
        </Portal>
      )}
      {!isEmpty(settingsPopupData) && (
        <Portal>
          <SettingsPopup popupName={POPUPS.SETTINGS_POPUP} />
        </Portal>
      )}
      {!isEmpty(leavePopupData) && (
        <Portal>
          <LeavePopup popupName={POPUPS.LEAVE_POPUP} />
        </Portal>
      )}
      {!isEmpty(mobileRequiredData) && (
        <Portal>
          <MobileRequiredPopup popupName={POPUPS.MOBILE_REQUIRED_POPUP} />
        </Portal>
      )}
      <StyledDesktopBar>
        <StyledBrand to="/home" aria-label="The Open League — Lobby">
          <img src={tolLogo} alt="The Open League" />
        </StyledBrand>
        <StyledHeaderRight>
          <StyledHeaderLeft>
            <StyledCrestLink to="/home" aria-label="Home">
              {crest ? <img src={crest} alt="" /> : <span>TOL</span>}
            </StyledCrestLink>
            <StyledTeamBlock>
              <ClubSwitcher variant="header" />
              {renderMetaRow()}
            </StyledTeamBlock>
          </StyledHeaderLeft>
          <StyledDropdownWrapper>
            <NotificationBell />
            {isAuthenticated ? (
              <StyledDropdownTrigger type="button" onClick={toggleDropdown}>
                <StyledAvatar src={avatarUrl} alt="avatar" />
              </StyledDropdownTrigger>
            ) : (
              <StyledDiscordTrigger type="button" onClick={toggleDropdown}>
                <DiscordLogoIcon />
              </StyledDiscordTrigger>
            )}
            {isDropdownVisible && (
              <StyledDropdownContainer ref={ref}>
                <UserDropdown
                  extend
                  openInvitePopupCallback={() => {
                    openInvitePopup();
                  }}
                  openSettingsCallback={() => {
                    openSettingsPopup();
                  }}
                  logoutCallback={() => {
                    logout();
                  }}
                  leaveGameCallback={() => {
                    leaveGame();
                  }}
                  showTutorialCallback={() => {
                    showTutorial();
                  }}
                />
              </StyledDropdownContainer>
            )}
          </StyledDropdownWrapper>
        </StyledHeaderRight>
      </StyledDesktopBar>
      <StyledMobileBar>
        <StyledCrestLink to="/home" aria-label="Home">
          {crest ? <img src={crest} alt="" /> : <span>TOL</span>}
        </StyledCrestLink>
        <StyledTeamBlock>
          <StyledTeamName>{clubName}</StyledTeamName>
          {renderMetaRow()}
        </StyledTeamBlock>
        <NotificationBell />
        <Cloaser onClick={onClick} isOpen={isSideOpen} />
      </StyledMobileBar>
    </StyledHeader>
  );
});
